import React, { useState, useEffect } from 'react';
import { 
  FaAppleAlt, 
  FaHeartbeat, 
  FaWeight, 
  FaLeaf, 
  FaUtensils, 
  FaDownload, 
  FaBookmark, 
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaPrint,
  FaShareAlt,
  FaClock,
  FaFire,
  FaWater,
  FaRunning,
  FaUserMd,
  FaUsers
} from 'react-icons/fa';
import { GiMeal, GiFruitBowl, GiChickenLeg } from 'react-icons/gi';

// Firebase imports
import { database } from '../firebase';
import { ref as dbRef, get } from 'firebase/database';

const Dietplan = () => {
  const [selectedDisease, setSelectedDisease] = useState('diabetes');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get disease-specific configuration
  const getDiseaseConfig = (diseaseName) => {
    const name = diseaseName.toLowerCase();
    
    if (name.includes('diabetes')) {
      return {
        icon: <FaAppleAlt className="text-3xl" />,
        color: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200',
        buttonColor: 'from-red-500 to-pink-500'
      };
    } else if (name.includes('blood pressure') || name.includes('hypertension') || name.includes('bp')) {
      return {
        icon: <FaHeartbeat className="text-3xl" />,
        color: 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
        buttonColor: 'from-blue-500 to-indigo-500'
      };
    } else if (name.includes('heart')) {
      return {
        icon: <FaHeartbeat className="text-3xl" />,
        color: 'bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200',
        buttonColor: 'from-pink-500 to-rose-500'
      };
    } else if (name.includes('weight') || name.includes('obesity')) {
      return {
        icon: <FaWeight className="text-3xl" />,
        color: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200',
        buttonColor: 'from-green-500 to-emerald-500'
      };
    } else if (name.includes('thyroid')) {
      return {
        icon: <FaLeaf className="text-3xl" />,
        color: 'bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200',
        buttonColor: 'from-purple-500 to-violet-500'
      };
    } else {
      return {
        icon: <FaUtensils className="text-3xl" />,
        color: 'bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200',
        buttonColor: 'from-teal-500 to-cyan-500'
      };
    }
  };

  // Fetch diet plans from Firebase
  useEffect(() => {
    const fetchDietPlans = async () => {
      try {
        setLoading(true);
        const dietPlansRef = dbRef(database, 'Dr/dietPlans');
        const snapshot = await get(dietPlansRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          
          // Transform Firebase data to match UI structure
          const transformedPlans = Object.keys(data).map(key => {
            const plan = data[key];
            
            // Map disease names to IDs and colors
            const diseaseConfig = getDiseaseConfig(plan.diseaseName);
            
            return {
              id: plan.planId || key,
              firebaseKey: key,
              title: `${plan.diseaseName} Diet Plan`,
              description: plan.description || 'Customized diet plan for better health',
              duration: `${plan.duration} Days Plan`,
              difficulty: plan.dietType || 'Moderate',
              icon: diseaseConfig.icon,
              color: diseaseConfig.color,
              buttonColor: diseaseConfig.buttonColor,
              details: {
                description: plan.description || '',
                importance: `This diet plan is designed by ${plan.doctorName} for ${plan.ageGroup}.`,
                dailyMealPlan: {
                  breakfast: plan.breakfast || [],
                  lunch: plan.lunch || [],
                  eveningSnack: plan.snacks || [],
                  dinner: plan.dinner || []
                },
                foodsToEat: plan.foodsAllowed || [],
                foodsToAvoid: plan.foodsToAvoid || [],
                extraTips: plan.notes || [],
                calorieTarget: "Consult with your doctor",
                doctorName: plan.doctorName,
                ageGroup: plan.ageGroup,
                dietType: plan.dietType,
                planCode: plan.planCode
              }
            };
          });
          
          setDietPlans(transformedPlans);
        } else {
          setDietPlans([]);
        }
      } catch (err) {
        console.error('Error fetching diet plans:', err);
        setError('Failed to load diet plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDietPlans();
  }, []);


  // Disease categories for menu - dynamically generated from fetched plans
  const diseaseCategories = [
    { id: 'all', name: 'All Diet Plans', icon: <FaUtensils />, color: 'from-teal-500 to-cyan-500', patients: 'All' },
    ...dietPlans.slice(0, 5).map(plan => ({
      id: plan.id,
      name: plan.title.replace(' Diet Plan', ''),
      icon: plan.icon,
      color: plan.buttonColor,
      patients: plan.details.ageGroup
    }))
  ];

  const handleDiseaseSelect = (diseaseId) => {
    setSelectedDisease(diseaseId);
    if (diseaseId !== 'all') {
      const plan = dietPlans.find(p => p.id === diseaseId);
      setSelectedPlan(plan);
    } else {
      setSelectedPlan(null);
    }
  };

  const handleBackToList = () => {
    setSelectedPlan(null);
  };

  const handleDownloadPlan = () => {
    alert('Diet plan PDF download started!');
  };

  const handleSaveForLater = () => {
    alert('Diet plan saved to your profile!');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading diet plans...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center bg-red-50 border border-red-200 rounded-xl p-8 max-w-md">
          <FaTimesCircle className="text-red-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error Loading Diet Plans</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Show empty state
  if (dietPlans.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <FaAppleAlt className="text-gray-300 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Diet Plans Available</h2>
          <p className="text-gray-600">Please check back later or contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Minimal Mobile Header */}
  

      {/* Desktop Header */}
      <section className="hidden mb-5 lg:block bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 p-2 rounded-lg">
                <FaAppleAlt className="text-2xl" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold">Diet Plans</h1>
                <p className="text-teal-100 text-sm">
                  Expert-designed nutrition plans for health conditions
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <div className="bg-white/20 px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FaBookmark className="text-lg" />
                  <div>
                    <div className="text-xs text-teal-100">Save Plans</div>
                    <div className="font-bold text-sm">4 categories</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Responsive Padding */}
      <div className={`${!selectedPlan ? 'pt-5 lg:pt-0' : 'pt-5 lg:pt-0'} px-3 sm:px-4 md:px-6 lg:px-8 pb-8`}>
        <div className="max-w-7xl mx-auto">
          {/* Hero Banner */}
          <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden mb-6 lg:mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-700 to-emerald-700 opacity-90"></div>
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{
                backgroundImage: 'url("https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80")'
              }}
            ></div>
            
            <div className="relative p-6 sm:p-8 lg:p-12">
              <div className="max-w-3xl">
                <div className="inline-flex items-center mb-4">
                  <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm mr-4">
                    <FaAppleAlt className="text-2xl sm:text-3xl text-white" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Healthy Diet Plans</h1>
                </div>
                
                <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-6 leading-relaxed">
                  Expert-designed nutrition plans tailored for specific health conditions
                </p>
              </div>
            </div>
          </div>

          {/* Disease Menu Tabs */}
          <div className="mb-6 lg:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg lg:text-xl font-bold text-gray-800">Select Disease Category</h2>
              <button className="text-teal-600 hover:text-teal-700 text-sm font-medium">
                View All →
              </button>
            </div>

            <div className="flex overflow-x-auto pb-2 space-x-3 hide-scrollbar">
              {diseaseCategories.map((disease) => (
                <button
                  key={disease.id}
                  onClick={() => handleDiseaseSelect(disease.id)}
                  className={`flex-shrink-0 flex items-center p-3 rounded-lg min-w-[120px] transition-all duration-300 ${
                    selectedDisease === disease.id 
                      ? `bg-gradient-to-r ${disease.color} text-white shadow-md`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className={`text-lg mr-2 ${selectedDisease === disease.id ? 'text-white' : ''}`}>
                    {disease.icon}
                  </div>
                  <span className="font-medium text-sm text-center">{disease.name}</span>
                </button>
              ))}
            </div>
          </div>

          {!selectedPlan ? (
            /* Diet Plans List Section */
            <>
              <div className="mb-8 lg:mb-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {dietPlans.map((plan) => (
                    <div key={plan.id} className={`${plan.color} border rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-md transition-all duration-300`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className={`bg-gradient-to-r ${plan.buttonColor} w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center`}>
                          {plan.icon}
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className="px-2 py-1 bg-white/50 rounded-full text-xs font-medium">
                            <FaClock className="inline mr-1" /> {plan.duration}
                          </span>
                        </div>
                      </div>
                      
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">{plan.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-4">{plan.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <button 
                          onClick={() => {
                            setSelectedPlan(plan);
                            setSelectedDisease(plan.id);
                          }}
                          className={`text-white px-4 py-2 rounded-lg bg-gradient-to-r ${plan.buttonColor} hover:opacity-90 transition-opacity text-xs sm:text-sm`}
                        >
                          View Plan
                        </button>
                        <button 
                          onClick={handleSaveForLater}
                          className="text-gray-500 hover:text-teal-600 transition-colors"
                          title="Save for later"
                        >
                          <FaBookmark size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* Diet Plan Details Section */
            <>
              {/* Back Button */}
              <div className="mb-4">
                <button
                  onClick={handleBackToList}
                  className="flex items-center text-teal-600 hover:text-teal-700 transition-colors text-sm"
                >
                  <FaArrowLeft className="mr-2" />
                  Back to All Plans
                </button>
              </div>

              {/* Plan Header */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-md border border-gray-200 overflow-hidden mb-6 lg:mb-8">
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                        {selectedPlan.title}
                      </h1>
                      <p className="text-gray-600 text-sm sm:text-base">{selectedPlan.description}</p>
                    </div>
                    <div className={`mt-4 sm:mt-0 bg-gradient-to-r ${selectedPlan.buttonColor} text-white p-4 rounded-xl`}>
                      <div className="text-2xl mb-1">{selectedPlan.icon}</div>
                      <div className="text-xs opacity-90">{selectedPlan.duration}</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <button
                      onClick={handleDownloadPlan}
                      className="flex items-center bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm"
                    >
                      <FaDownload className="mr-2" />
                      Download PDF
                    </button>
                    <button
                      onClick={handleSaveForLater}
                      className="flex items-center bg-white border border-teal-500 text-teal-500 px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors text-sm"
                    >
                      <FaBookmark className="mr-2" />
                      Save
                    </button>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center text-gray-600 mb-1">
                        <FaFire className="mr-2 text-sm" />
                        <span className="font-medium text-xs sm:text-sm">Calorie Target</span>
                      </div>
                      <div className="font-bold text-gray-800 text-sm sm:text-base">{selectedPlan.details.calorieTarget}</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center text-gray-600 mb-1">
                        <FaWater className="mr-2 text-sm" />
                        <span className="font-medium text-xs sm:text-sm">Water Intake</span>
                      </div>
                      <div className="font-bold text-gray-800 text-sm sm:text-base">3-4 Liters</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center text-gray-600 mb-1">
                        <FaRunning className="mr-2 text-sm" />
                        <span className="font-medium text-xs sm:text-sm">Exercise Required</span>
                      </div>
                      <div className="font-bold text-gray-800 text-sm sm:text-base">30 mins/day</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Details */}
              <div className="space-y-6">
                {/* Daily Meal Plan */}
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Daily Meal Plan</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Breakfast */}
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <div className="flex items-center mb-3">
                        <div className="bg-yellow-100 p-2 rounded-lg mr-2">
                          <span className="text-lg">🌅</span>
                        </div>
                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">Breakfast</h3>
                      </div>
                      <ul className="space-y-2">
                        {selectedPlan.details.dailyMealPlan.breakfast.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <FaCheckCircle className="text-green-500 mt-0.5 mr-2 text-xs flex-shrink-0" />
                            <span className="text-gray-700 text-xs sm:text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Lunch */}
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="bg-green-100 p-2 rounded-lg mr-2">
                          <span className="text-lg">🍱</span>
                        </div>
                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">Lunch</h3>
                      </div>
                      <ul className="space-y-2">
                        {selectedPlan.details.dailyMealPlan.lunch.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <FaCheckCircle className="text-green-500 mt-0.5 mr-2 text-xs flex-shrink-0" />
                            <span className="text-gray-700 text-xs sm:text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Evening Snack */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center mb-3">
                        <div className="bg-blue-100 p-2 rounded-lg mr-2">
                          <span className="text-lg">☕</span>
                        </div>
                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">Evening Snack</h3>
                      </div>
                      <ul className="space-y-2">
                        {selectedPlan.details.dailyMealPlan.eveningSnack.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <FaCheckCircle className="text-green-500 mt-0.5 mr-2 text-xs flex-shrink-0" />
                            <span className="text-gray-700 text-xs sm:text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Dinner */}
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="bg-purple-100 p-2 rounded-lg mr-2">
                          <span className="text-lg">🌙</span>
                        </div>
                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">Dinner</h3>
                      </div>
                      <ul className="space-y-2">
                        {selectedPlan.details.dailyMealPlan.dinner.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <FaCheckCircle className="text-green-500 mt-0.5 mr-2 text-xs flex-shrink-0" />
                            <span className="text-gray-700 text-xs sm:text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Foods to Eat & Avoid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* Foods to Eat */}
                  <div className="bg-green-50 rounded-xl p-4 sm:p-6 border border-green-200">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                      Foods to Eat ✅
                    </h2>
                    <ul className="space-y-3">
                      {selectedPlan.details.foodsToEat.map((food, index) => (
                        <li key={index} className="flex items-center bg-white/50 p-3 rounded-lg">
                          <div className="bg-green-100 p-2 rounded-lg mr-3">
                            <FaCheckCircle className="text-green-500 text-sm" />
                          </div>
                          <span className="text-gray-800 font-medium text-sm sm:text-base">{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Foods to Avoid */}
                  <div className="bg-red-50 rounded-xl p-4 sm:p-6 border border-red-200">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                      Foods to Avoid ❌
                    </h2>
                    <ul className="space-y-3">
                      {selectedPlan.details.foodsToAvoid.map((food, index) => (
                        <li key={index} className="flex items-center bg-white/50 p-3 rounded-lg">
                          <div className="bg-red-100 p-2 rounded-lg mr-3">
                            <FaTimesCircle className="text-red-500 text-sm" />
                          </div>
                          <span className="text-gray-800 font-medium text-sm sm:text-base">{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Extra Tips Section */}
                {selectedPlan.details.extraTips && selectedPlan.details.extraTips.length > 0 && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 sm:p-6 border border-amber-200">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <FaCheckCircle className="text-amber-600 mr-2" />
                      Additional Tips & Recommendations
                    </h2>
                    <ul className="space-y-3">
                      {selectedPlan.details.extraTips.map((tip, index) => (
                        <li key={index} className="flex items-start bg-white/50 p-3 rounded-lg">
                          <div className="bg-amber-100 p-2 rounded-lg mr-3 flex-shrink-0">
                            <FaCheckCircle className="text-amber-600 text-sm" />
                          </div>
                          <span className="text-gray-800 font-medium text-sm sm:text-base">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Plan Information */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Plan Information</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <FaUserMd className="text-teal-600 mr-3 text-xl" />
                      <div>
                        <p className="text-xs text-gray-500">Designed By</p>
                        <p className="font-semibold text-gray-800">{selectedPlan.details.doctorName}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaUsers className="text-teal-600 mr-3 text-xl" />
                      <div>
                        <p className="text-xs text-gray-500">Age Group</p>
                        <p className="font-semibold text-gray-800">{selectedPlan.details.ageGroup}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaUtensils className="text-teal-600 mr-3 text-xl" />
                      <div>
                        <p className="text-xs text-gray-500">Diet Type</p>
                        <p className="font-semibold text-gray-800">{selectedPlan.details.dietType}</p>
                      </div>
                    </div>
                    {selectedPlan.details.planCode && (
                      <div className="flex items-center">
                        <FaBookmark className="text-teal-600 mr-3 text-xl" />
                        <div>
                          <p className="text-xs text-gray-500">Plan Code</p>
                          <p className="font-semibold text-gray-800">{selectedPlan.details.planCode}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Dietplan;